import { DEFAULT_TASKLIST_ITEMS_PER_PAGE } from './tasklistConstants';

/**
 * Build ordered task id list saved when opening task detail from the list.
 * @param {object} listViewState
 * @returns {string[]}
 */
export function getDetailNavigationTaskIds(listViewState) {
  const ids = listViewState?.detailNavigationTaskIds;
  return Array.isArray(ids) ? ids.filter(Boolean) : [];
}

/**
 * Current index within detailNavigationTaskIds (-1 if unknown).
 */
export function getDetailNavigationIndex(listViewState, taskId) {
  if (typeof listViewState?.detailNavigationIndex === 'number') {
    return listViewState.detailNavigationIndex;
  }
  const ids = getDetailNavigationTaskIds(listViewState);
  return ids.indexOf(taskId);
}

export function getItemsPerPageFromState(listViewState) {
  const n = listViewState?.itemsPerPage;
  return typeof n === 'number' && n > 0 ? n : DEFAULT_TASKLIST_ITEMS_PER_PAGE;
}

export function getCurrentPageFromState(listViewState) {
  const page = listViewState?.detailNavigationPage;
  return typeof page === 'number' && page > 0 ? page : 1;
}

/**
 * Tasks on the current list page for Quick Peek.
 */
export function getQuickPeekTasks({ taskIds, tasksById, page, itemsPerPage, currentTaskId }) {
  const start = (page - 1) * itemsPerPage;
  const slice = taskIds.slice(start, start + itemsPerPage);
  return slice
    .map((id) => tasksById[id])
    .filter(Boolean)
    .map((task) => ({
      ...task,
      _isPeekSelected: task._id === currentTaskId,
    }));
}

/**
 * Resolve prev/next task id within saved navigation list.
 */
export function getAdjacentTaskId(listViewState, taskId, direction) {
  const ids = getDetailNavigationTaskIds(listViewState);
  if (!ids.length) return null;

  let index = getDetailNavigationIndex(listViewState, taskId);
  if (index < 0) return null;

  const nextIndex = direction === 'next' ? index + 1 : index - 1;
  if (nextIndex < 0 || nextIndex >= ids.length) return null;
  return ids[nextIndex];
}

/**
 * When crossing page boundary, compute new page + task id.
 */
export function getPageBoundaryNavigation(listViewState, taskId, direction) {
  const ids = getDetailNavigationTaskIds(listViewState);
  if (!ids.length) return null;

  const itemsPerPage = getItemsPerPageFromState(listViewState);
  let index = getDetailNavigationIndex(listViewState, taskId);
  if (index < 0) return null;

  const nextIndex = direction === 'next' ? index + 1 : index - 1;
  if (nextIndex >= 0 && nextIndex < ids.length) {
    return {
      taskId: ids[nextIndex],
      page: Math.floor(nextIndex / itemsPerPage) + 1,
      index: nextIndex,
    };
  }
  return null;
}

/**
 * Snapshot to persist when navigating list → task detail.
 */
export function buildDetailNavigationSnapshot({ filteredTasks, taskId, page, itemsPerPage }) {
  const list = Array.isArray(filteredTasks) ? filteredTasks : [];
  const taskIds = list.map((t) => t?._id).filter(Boolean);
  const index = taskIds.indexOf(taskId);
  const resolvedPage = typeof page === 'number' && page > 0
    ? page
    : (index >= 0 ? Math.floor(index / (itemsPerPage || DEFAULT_TASKLIST_ITEMS_PER_PAGE)) + 1 : 1);

  return {
    detailNavigationTaskIds: taskIds,
    detailNavigationIndex: index >= 0 ? index : -1,
    detailNavigationPage: resolvedPage,
    itemsPerPage: itemsPerPage || DEFAULT_TASKLIST_ITEMS_PER_PAGE,
  };
}
